import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import JsonLd, { webAppSchema } from 'shared/JsonLd';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

// Per-user monthly rates mirror the IT Services pricing tiers.
const TIERS = {
    Essential: { rate: 125, blurb: 'Helpdesk, monitoring, patching & endpoint protection.' },
    Professional: { rate: 185, blurb: 'Adds cloud, backup, MDR, and network administration.' },
    Complete: { rate: 275, blurb: '24/7 SOC, compliance support, and a virtual CIO.' },
};
const SERVER_RATE = 99;   // per managed server / month
const SITE_RATE = 149;    // per additional location / month

// Volume discount on the per-user rate.
const volumeDiscount = (users) => (users >= 50 ? 0.15 : users >= 25 ? 0.10 : users >= 10 ? 0.05 : 0);

const fmt = (n) => '$' + Math.round(n).toLocaleString();

const ManagedItCalculatorPage = () => {
    usePageSeo({
        title: 'Managed IT Cost Calculator — Estimate Your Monthly IT Spend',
        description: 'Free managed IT cost calculator. Estimate monthly and annual IT cost by users, servers, and plan tier — Essential, Professional, or Complete. By BUILDLAB.',
        keywords: 'managed IT cost calculator, managed services pricing, IT support cost, per user IT pricing, managed IT estimate, MSP pricing California, BUILDLAB',
    });

    const [users, setUsers] = useState(15);
    const [tier, setTier] = useState('Professional');
    const [servers, setServers] = useState(2);
    const [sites, setSites] = useState(1);

    const calc = useMemo(() => {
        const baseRate = TIERS[tier].rate;
        const disc = volumeDiscount(users);
        const effRate = baseRate * (1 - disc);
        const usersCost = effRate * users;
        const serverCost = servers * SERVER_RATE;
        const siteCost = Math.max(0, sites - 1) * SITE_RATE;
        const monthly = usersCost + serverCost + siteCost;
        return { baseRate, disc, effRate, usersCost, serverCost, siteCost, monthly, annual: monthly * 12 };
    }, [users, tier, servers, sites]);

    const Row = ({ label, value, sub }) => (
        <div className="d-flex justify-content-between align-items-baseline" style={{ padding: '8px 0', borderBottom: '1px solid var(--bs-border-color)' }}>
            <span style={{ color: 'var(--bs-body-color)' }}>{label}{sub && <span style={{ fontSize: '.78rem', color: 'var(--bs-secondary-color)' }}> {sub}</span>}</span>
            <span style={{ fontWeight: 600, color: 'var(--bs-dark)' }}>{value}</span>
        </div>
    );

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Managed IT Cost Calculator'} pagesub={'Calculator'} pageTop={'IT Services'} />
                <JsonLd data={webAppSchema({ name: 'Managed IT Cost Calculator', description: 'Free managed IT cost calculator — estimate monthly and annual IT cost by users, servers, and plan tier.', path: '/it-cost-calculator' })} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="text-center mb-5" style={{ maxWidth: 720, margin: '0 auto' }}>
                            <h2 className="mb-3">Estimate your managed IT cost in seconds</h2>
                            <p className="m-0 text-muted">
                                Move the sliders to size your environment and see an instant monthly and annual estimate.
                                Flat per-user pricing with volume discounts — no surprise invoices.
                            </p>
                        </div>

                        <div className="row justify-content-center">
                            {/* Inputs */}
                            <div className="col-lg-6 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '28px 30px' }}>
                                    <div className="mb-4">
                                        <label className="d-flex justify-content-between input_title">
                                            <span>Number of users</span><strong>{users}</strong>
                                        </label>
                                        <input type="range" min="1" max="200" step="1" value={users} onChange={(e) => setUsers(parseInt(e.target.value, 10))} className="w-100" aria-label="Number of users" />
                                    </div>

                                    <div className="mb-4">
                                        <label className="input_title d-block mb-2">Plan tier</label>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {Object.keys(TIERS).map((t) => (
                                                <button type="button" key={t} onClick={() => setTier(t)}
                                                    style={{
                                                        flex: 1, minWidth: 110, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '.85rem',
                                                        border: `1px solid ${tier === t ? 'var(--bs-primary)' : 'var(--bs-border-color)'}`,
                                                        background: tier === t ? 'var(--bs-primary)' : '#fff', color: tier === t ? '#fff' : 'var(--bs-dark)',
                                                    }}>
                                                    {t}<br /><span style={{ fontWeight: 400, fontSize: '.75rem' }}>${TIERS[t].rate}/user</span>
                                                </button>
                                            ))}
                                        </div>
                                        <p className="mt-2 mb-0" style={{ fontSize: '.82rem', color: 'var(--bs-secondary-color)' }}>{TIERS[tier].blurb}</p>
                                    </div>

                                    <div className="mb-4">
                                        <label className="d-flex justify-content-between input_title">
                                            <span>Managed servers</span><strong>{servers}</strong>
                                        </label>
                                        <input type="range" min="0" max="20" step="1" value={servers} onChange={(e) => setServers(parseInt(e.target.value, 10))} className="w-100" aria-label="Number of managed servers" />
                                        <span style={{ fontSize: '.78rem', color: 'var(--bs-secondary-color)' }}>${SERVER_RATE}/server/mo</span>
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between input_title">
                                            <span>Office locations</span><strong>{sites}</strong>
                                        </label>
                                        <input type="range" min="1" max="10" step="1" value={sites} onChange={(e) => setSites(parseInt(e.target.value, 10))} className="w-100" aria-label="Number of locations" />
                                        <span style={{ fontSize: '.78rem', color: 'var(--bs-secondary-color)' }}>First site included · ${SITE_RATE}/mo per additional</span>
                                    </div>
                                </div>
                            </div>

                            {/* Output */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', height: '100%' }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>Estimated monthly cost</div>
                                    <div style={{ fontSize: '2.8rem', fontWeight: 800, lineHeight: 1.1, color: '#fff' }}>{fmt(calc.monthly)}<span style={{ fontSize: '1rem', fontWeight: 500, opacity: 0.8 }}>/mo</span></div>
                                    <div style={{ fontSize: '.9rem', opacity: 0.85, marginBottom: 18 }}>≈ {fmt(calc.annual)} per year</div>

                                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 16px' }}>
                                        <Row label={`${users} users × ${fmt(calc.effRate)}`} value={fmt(calc.usersCost)} sub={calc.disc ? `(${Math.round(calc.disc * 100)}% volume off)` : ''} />
                                        {calc.serverCost > 0 && <Row label={`${servers} servers × $${SERVER_RATE}`} value={fmt(calc.serverCost)} />}
                                        {calc.siteCost > 0 && <Row label={`${sites - 1} extra location(s)`} value={fmt(calc.siteCost)} />}
                                    </div>

                                    <Link to="/contact" className="btn btn-primary w-100 mt-4" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Get an exact quote">Get an exact quote</span>
                                    </Link>
                                    <p className="mb-0 mt-3" style={{ fontSize: '.76rem', opacity: 0.7 }}>
                                        Estimate only. Your exact quote is confirmed after a free assessment of your environment.
                                    </p>
                                    <ToolLeadCapture
                                        tool="Managed IT Cost Calculator"
                                        summary={`${users} users · ${servers} servers · ${sites} site(s) · ${tier} plan — est. ${fmt(calc.monthly)}/mo (${fmt(calc.annual)}/yr)`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="text-center mt-4" style={{ fontSize: '.85rem' }}>
                            See what each plan includes on the <Link to="/it-services">IT Services</Link> page.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default ManagedItCalculatorPage;
